import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import StatusBadge from '@/components/ui/StatusBadge';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { usePerms } from '@/lib/perms';
import { specsApi, type SpecSummary } from '@/lib/specs';
import { aed, num, shortDate } from '@/lib/format';

const statusTone = (status: string) => {
  if (status === 'Won') return 'primary';
  if (status === 'Quoted') return 'secondary';
  if (status === 'Lost' || status === 'Cancelled') return 'danger';
  return 'neutral';
};

/** Список спецификаций на данных сервера (этап 1.3): итоги, маржа, нужные согласования; «Новая спецификация» создаёт черновик. */
export default function SpecificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePerms();
  const canEdit = can('specifications', 'write');
  const [rows, setRows] = useState<SpecSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    specsApi
      .list()
      .then(setRows)
      .catch((e) => setError((e as ApiError).code));
  }, []);

  const open = useMemo(() => (rows ?? []).filter((r) => r.status === 'Draft' || r.status === 'Quoted'), [rows]);
  const totalSale = open.reduce((s, r) => s + r.metrics.saleTotal, 0);
  const totalMargin = open.reduce((s, r) => s + r.metrics.productMargin, 0);
  const needApproval = open.filter((r) => r.metrics.approvalLevel).length;

  const create = async () => {
    setBusy(true);
    try {
      const s = await specsApi.create({});
      navigate(`/specifications/${s.id}`);
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('spec.eyebrow')}
        title={t('spec.title')}
        description={t('spec.description')}
        actions={
          <>
            <Link to="/catalog" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100">
              <i className="ri-grid-line text-[14px]" /> {t('spec.browseCatalog')}
            </Link>
            {canEdit && (
              <button type="button" disabled={busy} onClick={() => void create()} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50">
                <i className="ri-add-line text-[14px]" /> {t('spec.newSpec')}
              </button>
            )}
          </>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: t('spec.openProjects'), value: String(open.length), icon: 'ri-folder-3-line' },
          { label: t('spec.pipelineSale'), value: aed(totalSale), icon: 'ri-money-dollar-circle-line' },
          { label: t('spec.projectedMargin'), value: aed(totalMargin), icon: 'ri-line-chart-line' },
          { label: t('spec.needApproval'), value: String(needApproval), icon: 'ri-error-warning-line' },
        ].map((s) => (
          <div key={s.label} className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div>
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{s.label}</p>
              <p className="tnum mt-1 text-lg font-semibold text-foreground-950">{s.value}</p>
            </div>
            <i className={`${s.icon} text-[16px] text-foreground-300`} />
          </div>
        ))}
      </div>

      <Panel padded={false}>
        <div className="flex items-center justify-between border-b border-background-200 p-4">
          <h2 className="text-sm font-semibold text-foreground-950">{t('spec.allTitle')}</h2>
          <span className="text-[11px] text-foreground-400">{t('spec.sorted')}</span>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[900px] border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('spec.colSpec')}</th>
                <th className="px-3 py-2.5 font-medium">{t('spec.colClient')}</th>
                <th className="px-3 py-2.5 font-medium">{t('spec.colOwner')}</th>
                <th className="px-3 py-2.5 font-medium">{t('spec.colDelivery')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('spec.colLines')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('spec.colSale')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('spec.colMargin')}</th>
                <th className="px-4 py-2.5 font-medium">{t('spec.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((s) => (
                <tr key={s.id} onClick={() => navigate(`/specifications/${s.id}`)} className="group cursor-pointer border-b border-background-200/70 transition-colors last:border-0 hover:bg-primary-50/40">
                  <td className="px-4 py-3">
                    <p className="tnum text-[10.5px] text-foreground-500">{s.code}</p>
                    <p className="max-w-[280px] truncate font-medium text-foreground-900 group-hover:text-primary-700">{s.project || t('spec.untitled')}</p>
                    <p className="text-[10.5px] text-foreground-400">{t('spec.lineMeta', { lines: s.lineCount ?? 0, units: num(s.units ?? 0) })}</p>
                  </td>
                  <td className="px-3 py-3 text-foreground-700">{s.client || '—'}</td>
                  <td className="px-3 py-3 text-foreground-600">{s.ownerName || '—'}</td>
                  <td className="tnum px-3 py-3 text-foreground-700">{s.deliveryDate ? shortDate(s.deliveryDate) : '—'}</td>
                  <td className="tnum px-3 py-3 text-right text-foreground-700">{s.lineCount ?? 0}</td>
                  <td className="tnum px-3 py-3 text-right font-medium text-foreground-950">{aed(s.metrics.saleTotal)}</td>
                  <td className="px-3 py-3 text-right">
                    <span className="tnum font-medium text-primary-700">{aed(s.metrics.productMargin)}</span>
                    <span className="tnum block text-[10px] text-foreground-400">{s.metrics.productMarginPct != null ? `${s.metrics.productMarginPct.toFixed(0)}%` : '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <StatusBadge tone={statusTone(s.status) as 'primary' | 'secondary' | 'danger' | 'neutral'}>{t(`status.${s.status}`, s.status)}</StatusBadge>
                        {s.metrics.approvalLevel && (
                          <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-900" title={t(`sp.approvalNeeded.${s.metrics.approvalLevel}`)}>
                            <i className="ri-alert-line" />
                          </span>
                        )}
                      </div>
                      <i className="ri-arrow-right-s-line text-[15px] text-foreground-300 group-hover:text-primary-500" />
                    </div>
                  </td>
                </tr>
              ))}
              {rows !== null && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('spec.empty')}
                  </td>
                </tr>
              )}
              {rows === null && !error && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('ui.loading')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-1 border-t border-background-200 px-4 py-2.5 text-[11px] text-foreground-400 sm:flex-row sm:items-center sm:justify-between">
          <p>{t('spec.footer1', { count: rows?.length ?? 0, lines: num((rows ?? []).reduce((s, r) => s + (r.lineCount ?? 0), 0)) })}</p>
          <p>{t('spec.footer2')}</p>
        </div>
      </Panel>
      {error && <Toast message={t('ui.error')} detail={error} tone="error" onClose={() => setError(null)} />}
    </div>
  );
}
